import { TwitchStreamsService, type Stream } from '../lib/twitchStreams';
import { twitchService } from '../lib/twitch';
import { raidQueueService } from '../lib/raidQueue';
import { r3LabsService } from '../lib/r3dlabs';
import { formatDuration, formatViewerCount } from '../utils/formatters';
import { channelDetailsModal } from './ChannelDetailsModal';
import { StreamListHeader, type StreamFilters } from './StreamListHeader';
import Swal from 'sweetalert2';
import './OnlineComponent.css';

// Extended Stream interface to track source
interface ExtendedStream extends Stream {
  source?: 'following' | 'r3dlabs_teams' | 'both';
  teams?: Array<{
    team_id: string;
    team_name: string;
  }>;
}

export class OnlineComponent {
  private streamerList!: HTMLElement;
  private container: HTMLElement;
  private streams: ExtendedStream[] = [];
  private followingStreams: Stream[] = [];
  private teammateStreams: Stream[] = [];
  private filteredStreams: ExtendedStream[] = [];
  private streamsService: TwitchStreamsService;
  private selectedStreamId: string | null = null;
  private streamListHeader: StreamListHeader;
  private refreshInterval: number | null = null;
  private readonly REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes

  constructor(parentContainer: HTMLElement) {
    this.container = parentContainer;
    const elements = this.getRequiredElements();
    Object.assign(this, elements);

    // Initialize header with filter handlers
    this.streamListHeader = new StreamListHeader(
      elements.headerContainer,
      this.handleFiltersChange.bind(this),
      this.updateStreamList.bind(this)
    );

    const accessToken = twitchService.getAccessToken();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    this.streamsService = new TwitchStreamsService(
      import.meta.env.VITE_TWITCH_CLIENT_ID,
      accessToken
    );

    this.initializeEventListeners();
  }

  private getRequiredElements() {
    const onlineContainer = this.container.querySelector('.online-container');
    if (!onlineContainer) {
      throw new Error('Online container not found');
    }

    const streamerList = onlineContainer.querySelector('.streamer-list');
    if (!streamerList) {
      throw new Error('Streamer list not found');
    }

    // Add header container before streamer list
    const headerContainer = document.createElement('div');
    headerContainer.className = 'stream-list-header-container';
    streamerList.parentNode?.insertBefore(headerContainer, streamerList);

    return {
      streamerList: streamerList as HTMLElement,
      headerContainer: headerContainer as HTMLElement
    };
  }

  private initializeEventListeners(): void {
    // Streamer card click handlers
    this.streamerList.addEventListener('click', this.handleStreamerCardClick.bind(this));

    // Queue updates
    document.addEventListener('queueupdate', () => {
      const onlineTab = document.querySelector('#online.tab-content.active');
      if (onlineTab) {
        this.updateStreamList();
      }
    });
  }

  private handleFiltersChange(filters: StreamFilters): void {
    // Apply filters to streams
    let filtered: ExtendedStream[] = [...this.streams];

    // Apply dataset filter
    switch (filters.dataset) {
      case 'following':
        filtered = this.followingStreams.map(stream => ({ ...stream, source: 'following' } as ExtendedStream));
        break;
      case 'r3dlabs_teams':
        filtered = this.teammateStreams.map(stream => ({ ...stream, source: 'r3dlabs_teams' } as ExtendedStream));
        break;
      case 'all':
      default:
        filtered = [...this.streams]; // All combined streams
        break;
    }

    // Apply search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(stream => 
        stream.userName.toLowerCase().includes(searchLower) ||
        stream.title.toLowerCase().includes(searchLower)
      );
    }

    // Apply sort
    filtered.sort((a, b) => {
      const direction = filters.sort.direction === 'asc' ? 1 : -1;
      
      if (filters.sort.field === 'viewers') {
        return (a.viewerCount - b.viewerCount) * direction;
      } else { // duration
        return (new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime()) * direction;
      }
    });

    this.filteredStreams = filtered;
    this.renderStreams();
  }

  private async updateStreamList(): Promise<void> {
    try {
      console.log('Starting updateStreamList...');
      
      // Fetch both following streams and teammate streams
      const [followingStreams, teammateStreams] = await Promise.all([
        this.streamsService.getFollowedStreams(),
        this.fetchTeammateStreams()
      ]);

      console.log(`Following streams count: ${followingStreams.length}`);
      console.log(`Teammate streams count: ${teammateStreams.length}`);

      // Store separate arrays for filtering
      this.followingStreams = followingStreams;
      this.teammateStreams = teammateStreams;

      // Combine and deduplicate streams based on userId
      const allStreamsMap = new Map<string, ExtendedStream>();
      
      // Add following streams first
      followingStreams.forEach(stream => {
        allStreamsMap.set(stream.userId, { ...stream, source: 'following' } as ExtendedStream);
      });
      console.log(`Added ${followingStreams.length} following streams to map`);
      
      // Add teammate streams, but don't overwrite if already exists
      teammateStreams.forEach(stream => {
        const extendedTeammateStream = stream as ExtendedStream;
        if (!allStreamsMap.has(stream.userId)) {
          allStreamsMap.set(stream.userId, { ...extendedTeammateStream, source: 'r3dlabs_teams' } as ExtendedStream);
          console.log(`Added new teammate stream: ${stream.userName}`);
        } else {
          // Mark as both if it exists in both lists, and preserve team info
          const existingStream = allStreamsMap.get(stream.userId)!;
          existingStream.source = 'both';
          existingStream.teams = extendedTeammateStream.teams; // Add team info to the existing stream
          console.log(`Marked stream as 'both' sources: ${stream.userName}`);
        }
      });

      this.streams = Array.from(allStreamsMap.values());
      console.log(`Final combined streams count: ${this.streams.length}`);
      console.log('Final streams:', this.streams.map(s => ({ name: s.userName, source: s.source })));
      
      this.filteredStreams = [...this.streams]; // Initialize filtered streams
      
      // Apply current filters to new data
      this.handleFiltersChange(this.streamListHeader.getFilters());
    } catch (error) {
      console.error('Error updating streams:', error);
      
      // For auth errors or network errors, refresh the page
      if (error instanceof Error && (error.message.includes('auth_required') || error.message.includes('network_error'))) {
        console.log('Auth/Network error detected in OnlineComponent, refreshing page');
        window.location.reload();
        return;
      }
      
      await Swal.fire({
        title: 'Update Failed',
        text: 'Failed to update streams. Please try again.',
        icon: 'error',
        confirmButtonColor: '#9147ff',
        background: '#18181b',
        color: '#ffffff'
      });
    }
  }

  private renderStreams(): void {
    const allStreams = [...this.filteredStreams];
    
    // Split into queued and non-queued
    const queuedStreams = allStreams.filter(stream => 
      raidQueueService.isQueued(stream.userId)
    );
    const nonQueuedStreams = allStreams.filter(stream => 
      !raidQueueService.isQueued(stream.userId)
    );

    // Combine with queued streams first
    const sortedStreams = [...queuedStreams, ...nonQueuedStreams];

    // Render the streams
    this.streamerList.innerHTML = `
      <div class="live-section">
        ${sortedStreams.map(stream => 
          this.renderStreamCard(stream, raidQueueService.isQueued(stream.userId))
        ).join('')}
      </div>
    `;
  }

  private renderStreamCard(stream: ExtendedStream, isQueued: boolean): string {
    const isTeammate = stream.source === 'r3dlabs_teams' || stream.source === 'both';
    const teammateClass = isTeammate ? ' teammate' : '';
    
    // Render shared teams section for teammates
    const sharedTeamsSection = isTeammate && stream.teams && stream.teams.length > 0 ? `
      <div class="shared-teams-section">
        <div class="shared-teams-header">
          <span class="shared-teams-icon">👥</span>
          <span class="shared-teams-title">Shared Teams</span>
        </div>
        <div class="shared-teams-list">
          ${stream.teams.map(team => `
            <div class="shared-team-item">
              <span class="team-name">${team.team_name}</span>
            </div>
          `).join('')}
        </div>
      </div>
    ` : '';
    
    return `
      <div class="streamer-card${this.selectedStreamId === stream.userId ? ' active' : ''}${isQueued ? ' queued' : ''}${teammateClass}" 
        data-channel="${stream.userLogin}" 
        data-user-id="${stream.userId}">
        <div class="streamer-header">
          <div class="streamer-avatar">
            <img src="${stream.profileImageUrl}" alt="${stream.userName}">
            ${isTeammate ? '<div class="teammate-indicator">🤝</div>' : ''}
          </div>
          <div class="streamer-info">
            <div class="streamer-name-row">
              <div class="streamer-name">${stream.userName}</div>
              ${isQueued ? '<span class="queue-badge">In Queue</span>' : ''}
              ${isTeammate ? '<span class="teammate-badge">Teammate</span>' : ''}
            </div>
            <div class="stream-title">${stream.title}</div>
            <div class="stream-meta">
              <div class="viewer-count">
                <span>👥</span>
                ${formatViewerCount(stream.viewerCount)}
              </div>
              <div class="stream-duration">
                <span>🕒</span>
                ${formatDuration(stream.startedAt)}
              </div>
            </div>
          </div>
          <button class="queue-toggle-button" title="${isQueued ? 'Remove from queue' : 'Add to queue'}">
            ${isQueued ? '−' : '+'}
          </button>
        </div>
        <div class="stream-preview">
          <img src="${stream.thumbnailUrl}" alt="Stream Preview" class="preview-image">
          ${sharedTeamsSection}
          <div class="preview-actions">
            <button class="preview-button raid-button">Raid Now</button>
            <button class="preview-button details-button">Details</button>
          </div>
        </div>
      </div>
    `;
  }

  private async handleRaidButton(userId: string, userName: string, target: HTMLElement): Promise<void> {
    const result = await Swal.fire({
      title: 'Start Raid?',
      html: `Are you sure you want to raid <strong>${userName}</strong>?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, Start Raid!',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#9147ff',
      cancelButtonColor: '#6e7681',
      background: '#18181b',
      color: '#ffffff',
      width: '360px'
    });

    if (result.isConfirmed) {
      try {
        target.textContent = 'Starting Raid...';
        target.setAttribute('disabled', 'true');
        await this.streamsService.raidChannel(userId);
        
        await Swal.fire({
          title: 'Raid Started!',
          html: `Successfully started raid to <strong>${userName}</strong>`,
          icon: 'success',
          timer: 2000,
          timerProgressBar: true,
          confirmButtonColor: '#9147ff',
          background: '#18181b',
          color: '#ffffff',
          width: '360px'
        });

        this.selectedStreamId = null;

        setTimeout(() => {
          target.textContent = 'Raid Now';
          target.removeAttribute('disabled');
        }, 5000);
      } catch (error) {
        console.error('Failed to start raid:', error);
        
        const errorMessage = error instanceof Error ? error.message : 'Unable to start the raid. Please try again.';
        await Swal.fire({
          title: 'Unable to Start Raid',
          html: `<div style="text-align: left; margin-top: 1rem;">${errorMessage}</div>`,
          icon: 'error',
          confirmButtonColor: '#9147ff',
          background: '#18181b',
          color: '#ffffff',
          width: '360px'
        });

        target.textContent = 'Raid Now';
        target.removeAttribute('disabled');
      }
    }
  }

  private async handleStreamerCardClick(e: MouseEvent): Promise<void> {
    const target = e.target as HTMLElement;
    const card = target.closest('.streamer-card');
    
    if (!card) return;

    // Handle queue toggle button click
    if (target.classList.contains('queue-toggle-button')) {
      const userId = card.getAttribute('data-user-id');
      if (userId) {
        if (raidQueueService.isQueued(userId)) {
          raidQueueService.removeFromQueue(userId);
        } else {
          const stream = this.streams.find(s => s.userId === userId);
          if (stream) {
            raidQueueService.addToQueue(stream);
          }
        }
        // Clear selected stream when adding/removing from queue
        if (this.selectedStreamId === userId) {
          this.selectedStreamId = null;
          card.classList.remove('active');
        }
        this.renderStreams();
      }
      return;
    }

    if (target.classList.contains('raid-button')) {
      const userId = card.getAttribute('data-user-id');
      const userName = card.querySelector('.streamer-name')?.textContent;
      if (userId && userName) {
        await this.handleRaidButton(userId, userName, target);
      }
      return;
    }

    if (target.classList.contains('details-button')) {
      const userId = card.getAttribute('data-user-id');
      if (userId) {
        channelDetailsModal.show(userId);
      }
      return;
    }

    if (!target.closest('.preview-actions')) {
      const userId = card.getAttribute('data-user-id');
      if (userId) {
        if (this.selectedStreamId === userId) {
          this.selectedStreamId = null;
          card.classList.remove('active');
        } else {
          this.selectedStreamId = userId;
          this.streamerList.querySelectorAll('.streamer-card').forEach(c => 
            c.classList.remove('active')
          );
          card.classList.add('active');
        }
      }
    }
  }

  public async initialize(): Promise<() => void> {
    // Initial load
    await this.updateStreamList();
    
    console.log('Starting periodic refresh for Who\'s Online tab');
    
    // Set up periodic refresh
    this.refreshInterval = window.setInterval(() => {
      console.log('Periodic refresh of Who\'s Online data');
      // Only update if the tab is visible
      const onlineTab = document.querySelector('#online.tab-content.active');
      if (onlineTab) {
        this.updateStreamList();
      }
    }, this.REFRESH_INTERVAL);
    
    // Add visibility change listener to refresh when tab becomes visible
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        console.log('Document became visible, refreshing Who\'s Online data');
        const onlineTab = document.querySelector('#online.tab-content.active');
        if (onlineTab) {
          this.updateStreamList();
        }
      }
    });

    // Return cleanup function
    return () => {
      if (this.refreshInterval) {
        console.log('Cleaning up Who\'s Online refresh interval');
        clearInterval(this.refreshInterval);
        this.refreshInterval = null;
      }
    };
  }

  private async fetchTeammateStreams(): Promise<Stream[]> {
    try {
      const user = twitchService.getUser();
      console.log('Current Twitch user:', user);
      
      if (!user || !user.display_name) {
        console.log('No user or display name available for teammate fetch');
        return [];
      }

      console.log('Fetching teammates for user:', user.display_name);
      const teammatesResponse = await r3LabsService.getUserTeammates(user.display_name); // Get all teammates
      console.log('R3D Labs teammates response:', teammatesResponse);
      
      if (!teammatesResponse.data || teammatesResponse.data.length === 0) {
        console.log('No teammates data or empty array');
        return [];
      }

      console.log(`Found ${teammatesResponse.data.length} teammates from R3D Labs (checking their live status...)`);

      // Create a map to store team information by user ID
      const teammateTeamsMap = new Map<string, Array<{team_id: string, team_name: string}>>();
      
      // Group teams by teammate user ID and collect unique user IDs
      const uniqueUserIds = new Set<string>();
      teammatesResponse.data.forEach(teammate => {
        uniqueUserIds.add(teammate.teammate_platform_user_id);
        
        const teams = teammateTeamsMap.get(teammate.teammate_platform_user_id) || [];
        teams.push({
          team_id: teammate.team_id,
          team_name: teammate.team_name
        });
        teammateTeamsMap.set(teammate.teammate_platform_user_id, teams);
      });

      const userIdArray = Array.from(uniqueUserIds);
      console.log(`Fetching Twitch stream details for ${userIdArray.length} unique teammates in batch...`);

      // Get all stream details in batch instead of individual calls
      const streamDetailsList = await this.streamsService.getStreamsByUserIds(userIdArray);
      console.log(`Received ${streamDetailsList.length} live streams from batch request`);

      // Add team information to each stream
      const teammateStreams: Stream[] = streamDetailsList.map(streamDetails => {
        const extendedStream = {
          ...streamDetails,
          source: 'r3dlabs_teams',
          teams: teammateTeamsMap.get(streamDetails.userId) || []
        } as any;
        
        console.log(`Added teammate stream: ${streamDetails.userName} with teams:`, extendedStream.teams);
        return extendedStream;
      });

      console.log(`Final teammate streams count: ${teammateStreams.length}`);
      return teammateStreams;
    } catch (error) {
      console.error('Error fetching teammate streams:', error);
      return [];
    }
  }
}
